import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Spinner,
  Form,
  InputGroup,
} from "react-bootstrap";

import ApiService from "../../../services/ApiService";
import PageHeaderBar from "../../layouts/PageHeaderBar";
import { useDocumentTitle } from "../../utils";
import { timeAgo } from "../../../utils";

const AUTO_REFRESH_MS = 60000;

const CURRENCY_SYMBOLS = { USD: "$", GBP: "£", EUR: "€" };

function formatPrice(amount, currency) {
  if (amount === null || amount === undefined) return "—";
  const symbol = CURRENCY_SYMBOLS[currency] || "";
  const value = Number.isInteger(amount) ? amount : Number(amount).toFixed(2);
  return symbol ? `${symbol}${value}` : `${value} ${currency || ""}`;
}

// appends "(£xx.xx)" for non-GBP prices; gbpRates = { USD: 1.35, EUR: 1.16 }
// meaning 1 GBP buys that many units, so gbp = amount / rate
function gbpBracket(amount, currency, gbpRates) {
  if (
    amount === null ||
    amount === undefined ||
    !currency ||
    currency === "GBP" ||
    !gbpRates
  )
    return "";
  const rate = gbpRates[currency];
  if (!rate || rate <= 0) return "";
  return ` (£${(Number(amount) / rate).toFixed(2)})`;
}

function lastUpdatedLabel(dateValue) {
  if (!dateValue) return "never";
  return timeAgo(new Date(dateValue).getTime());
}

const PluginPricesDashboard = () => {
  useDocumentTitle("Plugin Sale Monitor");
  const [watches, setWatches] = useState([]);
  const [gbpRates, setGbpRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const api = new ApiService({});
      const res = await api.get("pluginSaleTracker/watches");
      if (res && Array.isArray(res.watches)) {
        setWatches(res.watches);
        if (res.gbpRates) setGbpRates(res.gbpRates);
      } else if (!silent) {
        toast.error(res?.error || "Failed to load plugin prices");
      }
    } catch (err) {
      if (!silent) toast.error(err?.message || "Failed to load plugin prices");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const id = setInterval(() => load({ silent: true }), AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const addPlugin = async (e) => {
    e.preventDefault();
    const url = newUrl.trim();
    if (!url) return;
    setAdding(true);
    try {
      const api = new ApiService({});
      // identify + first scrape can take a while (puppeteer + LLM on some sites)
      const res = await api.post(
        "pluginSaleTracker/addPlugin",
        { url },
        undefined,
        { timeout: 180000 }
      );
      if (res?.ok && res.watch) {
        toast.success(
          `Now monitoring ${res.watch.name} at ${res.watch.latestPriceDisplayValue}`
        );
        setNewUrl("");
        await load({ silent: true });
      } else {
        toast.error(res?.error || "Failed to add plugin");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to add plugin");
    } finally {
      setAdding(false);
    }
  };

  const scanNow = async () => {
    setScanning(true);
    try {
      const api = new ApiService({});
      const res = await api.post("pluginSaleTracker/scan", {});
      if (res?.started) {
        toast.success(
          "Scan started - prices will update over the next few minutes"
        );
      } else {
        toast.error(res?.error || "Failed to start scan");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to start scan");
    } finally {
      setScanning(false);
    }
  };

  const renderRow = (w) => {
    const failed = w.lastScrapeStatus === "failed";
    const cheapestReseller = (w.latestResellerFindings || [])[0] || null;
    return (
      <tr key={w.pluginKey}>
        <td>
          <a href={w.url} target="_blank" rel="noreferrer">
            <b>{w.name}</b>
          </a>
          <div className="text-muted small">{w.vendor}</div>
        </td>
        <td>
          {failed ? (
            <Badge bg="danger">scrape failed</Badge>
          ) : (
            <>
              <a
                href={w.url}
                target="_blank"
                rel="noreferrer"
                title="Go to the official product page"
              >
                <b>{w.latestPriceDisplayValue || "—"}</b>
              </a>
              <span className="text-muted">
                {gbpBracket(w.latestPriceAmount, w.latestPriceCurrency, gbpRates)}
              </span>
              {w.latestOnSale && (
                <>
                  {" "}
                  <Badge bg="success">on sale</Badge>
                </>
              )}
              {w.latestListPriceAmount ? (
                <div className="text-muted small">
                  <s>
                    {formatPrice(w.latestListPriceAmount, w.latestPriceCurrency)}
                  </s>
                  {gbpBracket(
                    w.latestListPriceAmount,
                    w.latestPriceCurrency,
                    gbpRates
                  )}{" "}
                  list
                </div>
              ) : null}
            </>
          )}
        </td>
        <td>
          <a
            href={w.lowestSeenUrl || w.url}
            target="_blank"
            rel="noreferrer"
            title="Go to the page with the lowest seen price"
          >
            <b>{formatPrice(w.lowestSeenAmount, w.currency)}</b>
          </a>
          <span className="text-muted">
            {gbpBracket(w.lowestSeenAmount, w.currency, gbpRates)}
          </span>
          {w.lowestSeenSource && w.lowestSeenSource !== "official" && (
            <div className="text-muted small">via {w.lowestSeenSource}</div>
          )}
        </td>
        <td>
          {cheapestReseller ? (
            <>
              {cheapestReseller.url ? (
                <a href={cheapestReseller.url} target="_blank" rel="noreferrer">
                  {formatPrice(cheapestReseller.price, cheapestReseller.currency)}
                </a>
              ) : (
                formatPrice(cheapestReseller.price, cheapestReseller.currency)
              )}
              <span className="text-muted">
                {gbpBracket(
                  cheapestReseller.price,
                  cheapestReseller.currency,
                  gbpRates
                )}
              </span>
              <div className="text-muted small">
                {cheapestReseller.site}
                {cheapestReseller.verificationStatus === "verified" && (
                  <>
                    {" "}
                    <Badge bg="success">verified</Badge>
                  </>
                )}
                {(cheapestReseller.verificationStatus === "unchecked" ||
                  !cheapestReseller.verificationStatus) && (
                  <>
                    {" "}
                    <Badge bg="secondary">unverified</Badge>
                  </>
                )}
                {cheapestReseller.verificationStatus === "unverifiable" && (
                  <>
                    {" "}
                    <Badge bg="warning" text="dark">
                      couldn't verify
                    </Badge>
                  </>
                )}
              </div>
            </>
          ) : (
            <span className="text-muted">—</span>
          )}
        </td>
        <td>
          <span title={w.latestPriceObservedAt || ""}>
            {lastUpdatedLabel(w.lastScrapeAt)}
          </span>
          {failed && w.consecutiveScrapeFailures > 0 && (
            <div className="text-danger small">
              {w.consecutiveScrapeFailures} fail
              {w.consecutiveScrapeFailures === 1 ? "" : "s"} in a row
            </div>
          )}
        </td>
        <td className="text-muted small">
          {(w.priceObservations || []).length} obs
          {w.lastScrapeStrategyUsed ? ` · ${w.lastScrapeStrategyUsed}` : ""}
        </td>
      </tr>
    );
  };

  return (
    <div className="global-dash-wrapper">
      <PageHeaderBar
        pages={[{ title: "Plugin Sale Monitor", url: "/plugins/dashboard" }]}
      />
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-0">Plugin Wishlist Prices</h4>
                <span className="text-muted small">
                  Scanned daily at 9am UK · alerts email when a price drops below
                  the lowest ever seen
                </span>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={scanNow}
                disabled={scanning}
              >
                {scanning ? "Starting…" : "Scan now"}
              </Button>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : watches.length === 0 ? (
                <p className="text-muted mb-0">
                  No price data yet — run the first scan with the button above or
                  wait for the daily scan.
                </p>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Plugin</th>
                      <th>Current Price</th>
                      <th>Lowest Seen</th>
                      <th>Cheapest Reseller</th>
                      <th>Last Updated</th>
                      <th>History</th>
                    </tr>
                  </thead>
                  <tbody>{watches.map(renderRow)}</tbody>
                </Table>
              )}
              <Form onSubmit={addPlugin} className="mt-3">
                <InputGroup>
                  <Form.Control
                    type="url"
                    placeholder="Paste a product page URL to monitor a new plugin, e.g. https://vendor.com/products/plugin"
                    value={newUrl}
                    onChange={(e) => setNewUrl(e.target.value)}
                    disabled={adding}
                  />
                  <Button
                    type="submit"
                    variant="success"
                    disabled={adding || !newUrl.trim()}
                  >
                    {adding ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-1"
                        />
                        Identifying…
                      </>
                    ) : (
                      "Add plugin"
                    )}
                  </Button>
                </InputGroup>
                <Form.Text muted>
                  The product name, vendor and current price are detected
                  automatically; today's price becomes the alert baseline.
                </Form.Text>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PluginPricesDashboard;
